import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { FormHelperText } from '@mui/material'
import Box from "@mui/material/Box";
import DirectionsIcon from '@mui/icons-material/Directions';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import IconButton from '@mui/material/IconButton';
import { useGoogleMap } from '@react-google-maps/api'
import { green, grey } from '@mui/material/colors';
import { titleCase } from 'title-case';
import UserContext from "../context/UserContext";
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import _ from 'lodash'
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

let vehclassifications = require('../modules/User/vehicleclass.json');

const ControlBox = styled('div')(({ theme }) => ({
    padding: 10, width: '100%', borderRadius: '5px', backgroundColor: theme.palette.background.default, boxShadow: "rgb(0 0 0 / 30%) 0px 1px 4px -1px", position: 'relative', zIndex: 1000
}));

const permitRequired = "Permit Required";
const selectVehicle = "Please Select a Vehicle";

const VehiclesSelection = (props) => {
    const vehicles = props.vehicles;
    const updateRouteLookup = props.updateRouteLookup;
    const routeLookup = props.routeLookup;
    const setDisableEntry = props.setDisableEntry;
    const [hasVehicle, setHasVehicle] = React.useState(routeLookup.vehicle.externalId !== "");
    const [showCheckbox, updateShowCheckbox] = React.useState(routeLookup.vehicle.permitApplied);
    const [hasPermit, setHasPermit] = React.useState(routeLookup.vehicle.permitApplied);

    const determinePermit = (routeLookup) => {
        const vehicle = routeLookup.vehicle;
        if (vehicle.externalId === "") return false;
        const width = vehicle.vehicleWidth;
        if (width.feet >= 8) {
            if(width.feet > 8) return true;
            if(width.feet === 8 && width.inches > 6) return true;
        }
        const height = vehicle.vehicleHeight;
        if (height.feet >= 13) {
            if(height.feet > 13) return true;
            if(height.feet === 13 && height.inches > 6) return true;
        }
        const vehicleClassification = vehclassifications.find(veh => veh.id === vehicle.type);
        if(vehicleClassification.minWeightMsg1 != null && vehicle.weightInLbs > vehicleClassification.minWeightMsg1) return true;
         if(vehicleClassification.minWeightMsg2 != null && vehicle.weightInLbs > vehicleClassification.minWeightMsg2) return true;
        return false;
    }

    const getCautionMessage = (vehicle) => {
        if (!vehicle) return null;
        const vehicleClassification = vehclassifications.find(veh => veh.id === vehicle.type);
        if (!vehicleClassification) return null;

        if (vehicle.weightInLbs >= vehicleClassification.minWeightMsg2) {
            return "Your vehicle exceeds the standard weight limit for travel across Chicago. Please visit https://ipi.cityofchicago.org/Permits and apply for a Truck Travel Permit. Permit Type Overweight Axle or Overweight or Oversized Vehicle. If you have already received a permit, please check the checkbox above to generate a route.";
          } else if (vehicle.weightInLbs >= vehicleClassification.minWeightMsg1 && vehicle.weightInLbs <= vehicleClassification.maxWeightMsg1) {
            return "Your vehicle exceeds a weight limit to access boulevards and other locations in the city. Please visit https://ipi.cityofchicago.org/Permits and apply for a Truck Travel Permit. Permit Type: Travel on Industrial Corridor or Travel on Lake Shore Drive and Boulevards. If you have already received a permit, please check the checkbox above to generate a route.";
          }

        const width = vehicle.vehicleWidth;
        const height = vehicle.vehicleHeight;
    
        const isOverDimensional =
            (width.feet > 8 || (width.feet === 8 && width.inches > 6)) ||
            (height.feet > 13 || (height.feet === 13 && height.inches > 6));
    
        if (isOverDimensional) {
            return "Your vehicle exceeds the standard size limit for travel across Chicago. Please visit https://ipi.cityofchicago.org/Permits and apply for a Truck Travel Permit. If you have already received a permit, please check the checkbox above to generate a route.";
        }
        return null; // No caution needed
    }

    React.useEffect(() => {
        if (routeLookup.vehicle.externalId === "") {
            setHasVehicle(false);
            updateShowCheckbox(false);
            setDisableEntry(true);
            setHasPermit(false);
        } else {
            setHasVehicle(true);
            if (routeLookup.hasPermit) {
                updateShowCheckbox(true);
                setHasPermit(true);
                setDisableEntry(false);
            } else {
                const permitNeeded = determinePermit(routeLookup);
                setDisableEntry(permitNeeded && !hasPermit);
                updateShowCheckbox(permitNeeded);
            }
        }
    }, [routeLookup, setDisableEntry])

    const update = (event, vehicles) => {
        const vehicleName = event.target.value;
        const vehicle = vehicles.find(v => v.externalId === vehicleName);
        if (vehicle) {
            updateRouteLookup(routeLookup => {
                return { ...routeLookup, vehicle: vehicle }
            })
        }
    }

    const handleCheckboxClick = (e) => {
        setHasPermit(e.target.checked);
        setDisableEntry(!e.target.checked);
        
        updateRouteLookup(routeLookup => ({
            ...routeLookup,
            vehicle: {
              ...routeLookup.vehicle,
              permitApplied: e.target.checked
            }
          }));
    }

    return (
        <Grid container spacing={1} sx={{ mb: 1, width: "100%", display: "flex" }}>
            <Grid item xs={9}>
                <FormControl sx={{ width: "100%" }} size="small">
                    <InputLabel id="select-label">Select a Vehicle</InputLabel>
                    <Select
                        labelId="vehicle-label"
                        id="vehicle-select"
                        label="Select a Vehicle"
                        onChange={(e) => update(e, vehicles)}
                        value={routeLookup.vehicle?.externalId}
                    >
                        {vehicles.map(vehicle => {
                            return <MenuItem value={vehicle.externalId} key={vehicle.externalId}>{vehicle.externalId}</MenuItem>
                        })}
                    </Select>
                    {/* <FormHelperText error={!hasVehicle || (showCheckbox && !hasPermit)} sx={{ fontSize: '0.65rem' }}>
                        {!hasVehicle ? selectVehicle : showCheckbox && !hasPermit ? permitRequired : ""}
                    </FormHelperText> */}
                </FormControl>
            </Grid>
            {showCheckbox &&
                <Grid item xs={3}>
                    <FormControlLabel control={<Checkbox size="small" checked={hasPermit} onClick={(e) => handleCheckboxClick(e)} />} label="Permit?" labelPlacement="bottom" />
                </Grid>
            }
            {showCheckbox && !hasPermit && (() => {
                const caution = getCautionMessage(routeLookup.vehicle);

                    return caution ? (
                        <Grid item xs={12}>
                            <Box sx={{ maxWidth: "100%" }}>
                                <Typography variant="subtitle2" sx={{ textAlign: "left", color:'red' }}>{permitRequired}</Typography>
                                <Typography variant="body2">{caution}</Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    <a href='https://ipi.cityofchicago.org/Permits'>https://ipi.cityofchicago.org/Permits</a>
                                </Typography>
                            </Box>
                        </Grid>
                    ) : null;
                })()
                // <Grid item xs={12}>
                //     <Box sx={{ maxWidth: "100%" }}>
                //         <Typography variant="subtitle1" sx={{ textAlign: "left" }}>CAUTION</Typography>
                //         <Typography variant="body2">Your vehicle is over the allowable limit for height, width or weight. You need to apply for a permit from the City of Chicago permitting website in order to legally complete this trip.</Typography>
                //         <Typography variant="body2" sx={{ mt: 1 }}><a href="https://ipi.cityofchicago.org/Profile">https://ipi.cityofchicago.org/Profile</a></Typography>
                //     </Box>
                // </Grid>
            }
        </Grid>
    )
}

const DirectionsControl = (props) => {
    const isTabletOrMobileDevice = useMediaQuery('(max-device-width: 700px)');

    if (isTabletOrMobileDevice) {
        return <DialogPopup {...props} />
    } else {
        return (
            <Box sx={{ ml: 2, mt: 1 }}>
                <DirectionsControlTabs {...props} />
            </Box>
        )
    }
}

const DialogPopup = (props) => {
    const [open, setOpen] = React.useState(true);

    const handleClose = () => {
        setOpen(false);
    }

    return (
        <Dialog onClose={handleClose} aria-labelledby="directions-dialog" open={open} keepMounted sx={{m:0, p:0}}>
            <CloseDialogTitle onClose={handleClose} />
            <DirectionsControlTabs {...props} />
        </Dialog>
    )
}

const CloseDialogTitle = (props) => {
    const { children, onClose, ...other } = props;
    const theme = useTheme();

    return (
        <DialogTitle sx={{ m: 0, p: 0, height: 20, backgroundColor: theme.palette.background.default }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 2,
                        top: 2,
                        color: (theme) => theme.palette.grey[500]
                       
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

const TabPanel = (props) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 0 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

const TurnByTurnPanel = (props) => {
    const routes = props.routes;
    const selectedRoutes = props.selectedRoutes;
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('md'));
    return (
        <Box sx={{ width: matches ? "500px" : "100%",
            maxHeight: "60vh", // Maximum height (adjust as needed)
            overflowY: "auto"  // Enables vertical scrolling when content overflows
         }}>
            {routes && routes.pathPolyLines && routes.pathPolyLines.map((path, idx) => {
               return <TurnByTurnDirections advice={path.advice} index={idx} key={idx} isChecked={selectedRoutes[idx]} />
            })
            }
        </Box>
    )
}


const TurnByTurnDirections = (props) => {
    let advice = props.advice.replaceAll(". ", "</li><li>");
    advice = "<ul><li>" + advice + "</li></ul>"
    const isChecked = props.isChecked;
    const index = props.index*1+1;

    if (isChecked) {
        return (
            <Box component="div">
                <Typography variant="subtitle1">{"Route " + index}</Typography>
                <Divider sx={{mb:1}}></Divider>
                <Typography variant="body1" dangerouslySetInnerHTML={{ __html: advice }} sx={{mb:2}}></Typography>
            </Box>

        )
    } else {
        return null;
    }
}

const DisclaimerPanel = (props) => {
    const routes = props.routes;
    const selectedRoutes = props.selectedRoutes;
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('md'));
    return (
        <Box sx={{ width: matches ? "500px" : "100%",
            maxHeight: "60vh", // Maximum height (adjust as needed)
            overflowY: "auto"  // Enables vertical scrolling when content overflows
         }}>
            <Typography variant="body1" sx={{mb:2}}>By using the Chicago Traffic Tracker website, you are accepting that any and all maps/data and information you may receive (the "data") "as is" and without any warranty or representation whatsoever, express or implied, whether for accuracy, timeliness, completeness, fitness for a particular purpose or otherwise. The information on this website is provided here for reference purposes only. While every effort is made to provide as much accurate information as possible, the City of Chicago, its departments, employees, or contractors may not be held liable for any inaccurate or incomplete information.</Typography>
        </Box>
    )
}

const DirectionsControlTabs = (props) => {
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('md'));
    const routes = props.routes;

    const [value, setValue] = React.useState(0);
    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    // Dynamically calculate index for Disclaimer tab
    const disclaimerIndex = routes ? 2 : 1;

    return (
        <ControlBox>
            <Box sx={{ width: '100%', maxHeight: matches ? "75%" : "100%"}}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs value={value} onChange={handleChange} aria-label="Find Routes">
                        <Tab label="Find Routes" />
                        {routes && <Tab label="Turn-by-Turn" />}
                        <Tab label="Disclaimer" />
                    </Tabs>
                </Box>
                <TabPanel value={value} index={0}><DirectionsControlLayout {...props} /></TabPanel>
                <TabPanel value={value} index={1}><TurnByTurnPanel {...props}/></TabPanel>
                <TabPanel value={value} index={disclaimerIndex}><DisclaimerPanel {...props}/></TabPanel>
            </Box>
        </ControlBox>
    )
}

const DirectionsControlLayout = (props) => {
    const [selected, setSelected] = React.useState("");
    const [roads, setRoads] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [disableEntry, setDisableEntry] = React.useState(true);
    const [disclaimer, setDisclaimer] = React.useState(false);
    const [showDisclaimerDialog, setShowDisclaimerDialog] = React.useState(false);
    const [pendingRouteLookup, setPendingRouteLookup] = React.useState(null);
    const [oDirection, setODirection] = React.useState(null);
    const [dDirection, setDDirection] = React.useState(null);
    const user = React.useContext(UserContext);
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('md'));
    const setRoutes = props.setRoutes;
    const setSelectedRoutes = props.setSelectedRoutes;
    const selectedRoutes = props.selectedRoutes;
    const updateRouteLookup = props.updateRouteLookup;
    const routeLookup = props.routeLookup;
    const clearRouteLookup = props.clearRouteLookup;
    const map = useGoogleMap();

    React.useEffect(() => {
        async function fetchData() {
            const result = await fetch(process.env.REACT_APP_API_HOST + "/route/roads");
            result
                .json()
                .then(res => {
                    setRoads(res);
                });
        }
        fetchData();
    }, [])

    const handleClear = () => {
        clearRouteLookup();
        setRoutes(null);
        setSelectedRoutes([])
        setError("");
        setSelected("");
        setDisclaimer(false);
        setODirection(null);
        setDDirection(null);
    };

    const handleFindClick = (routeLookup) => {
        setPendingRouteLookup(routeLookup);
        setShowDisclaimerDialog(true);
    };

    const handleDisclaimerAccept = () => {
        setShowDisclaimerDialog(false);
        if (pendingRouteLookup) {
            handleFind(pendingRouteLookup);
            setPendingRouteLookup(null);
        }
    };

    const handleDisclaimerCancel = () => {
        setShowDisclaimerDialog(false);
        setPendingRouteLookup(null);
    };

    const handleFind = (routeLookup) => {
        async function fetchData(routeLookup) {
            try {
                const result = await fetch(process.env.REACT_APP_API_HOST + "/route/routes", {
                    headers: {
                        "Content-Type": "application/json"
                    },
                    method: "PUT",
                    body: JSON.stringify(routeLookup)
                });
                result
                    .json()
                    .then(res => {

                        if (!Array.isArray(res.pathPolyLines)) {
                            throw new Error("Missing pathPolylines in response");
                          }

                        let cleanedPolylines = res.pathPolyLines.map(path => {
                            console.log("in poly map");
                            let poly = path.routePolyline;
                      
                            // Helper to check if a point has valid lat/lng
                            const isValidPoint = (point) =>
                              point &&
                            point.lat !== undefined &&
                            point.lng !== undefined &&
                            !isNaN(parseFloat(point.lat)) &&
                            !isNaN(parseFloat(point.lng));
                      
                            // Remove invalid first point
                            if (poly.length > 0 && !isValidPoint(poly[0])) {
                              poly.shift();
                            }
                      
                            // Remove invalid last point
                            if (poly.length > 0 && !isValidPoint(poly[poly.length - 1])) {
                              poly.pop();
                            }
                      
                            return {
                              ...path,
                              routePolyline: poly
                            };
                          });


                        if (res.pathPolyLines.length > 0) {
                            setError("")
                            setRoutes({ ...res, pathPolylines: cleanedPolylines }); //res
                            setDisclaimer(true);
                            setSelectedRoutes(Array(res.pathPolyLines.length).fill(true));
                            map.panTo(routeLookup.origin);
                            if (map.getZoom() < 12) {
                                map.setZoom(12);
                            }

                        } else {
                            setError("No Routes Found: Based on your vehicle information, route restrictions, and viable roadways, the automated system was unable to find a route between the origin and destination addresses. This may also be due to the inability to geocode the addresses entered. Try different nearby addresses to see if a route can be generated.")
                        }
                        setLoading(false);
                    }).catch(err => {
                        setLoading(false)
                        setError("Route Lookup Failed: Based on your vehicle information, route restrictions, and viable roadways, the automated system was unable to find a route between the origin and destination addresses. This may also be due to the inability to geocode the addresses entered. Try different nearby addresses to see if a route can be generated.")
                    })
            } catch (ex) {
                setLoading(false)
                setError("Route Lookup Failed: Based on your vehicle information, route restrictions, and viable roadways, the automated system was unable to find a route between the origin and destination addresses. This may also be due to the inability to geocode the addresses entered. Try different nearby addresses to see if a route can be generated.")
            }
        }
        let saveData = _.cloneDeep(routeLookup);
        delete saveData.originAddress.marker;
        delete saveData.destinationAddress.marker;
        delete saveData.originAddress.error;
        delete saveData.destinationAddress.error;
        delete saveData.origin.valid;
        delete saveData.destination.valid;
        delete saveData.destination.crossStreet;
        delete saveData.origin.crossStreet;
        delete saveData.hasPermit;
        setSelected("");
        setSelectedRoutes([]);
        setRoutes([]);
        setError("");
        setLoading(true);
        fetchData(saveData);
    }

    return (
        <>
            <Box  sx={{
                width: matches ? "500px" : "100%",
                maxHeight: matches ? `calc(70vh - 175px)` : "100%", 
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
            }}>
                <VehiclesSelection routeLookup={routeLookup} updateRouteLookup={updateRouteLookup} vehicles={user.extVehicles} setDisableEntry={setDisableEntry} />
                <AddressLookup title={"origin"} selected={selected} setSelected={setSelected}  direction={oDirection} setDirection={setODirection} {...props} routeLookup={routeLookup} updateRouteLookup={updateRouteLookup} roads={roads} disableEntry={disableEntry} />
                <AddressLookup title={"destination"} selected={selected} setSelected={setSelected} direction={dDirection} setDirection={setDDirection} {...props} routeLookup={routeLookup} updateRouteLookup={updateRouteLookup} roads={roads} disableEntry={disableEntry} />
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={1}>
                    <Grid item xs={12} md={4} sx={{display: "flex", justifyContent: "center", alignItems: "center", textAlign: matches ? "left" : "center" }}>
                        <Button variant="contained" onClick={() => handleFindClick(routeLookup)} disabled={loading || !routeLookup.origin.valid || !routeLookup.destination.valid}>Find</Button>
                        <Button variant="contained" sx={{ ml: 2 }} onClick={handleClear} disabled={loading}>Clear</Button>
                    </Grid>
                    {loading &&
                        <Grid item xs={12} md={8} sx={{ textAlign: "center" }}>
                            <CircularProgress />
                            <Typography variant="button" sx={{ verticalAlign: "50%", ml: 2 }}>Retrieving Routes</Typography>
                        </Grid>
                    }
                    {error !== "" &&
                        <Grid item xs={12} md={8} sx={{ textAlign: matches ? "left" : "center" }}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', verticalAlign: "top", color: "red" }}>{error}</Typography>
                        </Grid>
                    }
                    {!loading && selectedRoutes.length > 0 &&
                        <Grid item xs={12} md={8} sx={{ textAlign: "center" }}>
                            <RoadsSelection selectedRoutes={selectedRoutes} setSelectedRoutes={setSelectedRoutes} />
                        </Grid>
                    }
                </Grid>
                <Divider sx={{ mb: 1, mt: 1 }} />
                <Typography variant="body1" component="div" paragraph>
                        BETA 
                </Typography>
                {disclaimer && 
                    <Typography   variant="body1"
                        component="div"
                        paragraph
                        sx={{ fontSize: '0.6rem', color: 'text.secondary' }}
                        >
                            DISCLAIMER: The information on this website is provided here for reference purpose only. While every effort is employed to provide as much accurate information as possible, City of Chicago, any of its departments, or any of its employees cannot be held liable for the accuracy and completeness of this information. Speed and congestion estimates are probabilistic and are not actual speed. Occasionally outlier data or unexpected behavior of a probe vehicle can skew the segment speed to be higher or lower than the ground truth. 
                    </Typography>
                }
                <Dialog open={showDisclaimerDialog} onClose={handleDisclaimerCancel} aria-labelledby="routing-disclaimer-dialog">
                    <DialogTitle id="routing-disclaimer-dialog">Routing Notice</DialogTitle>
                    <Box sx={{ px: 3, pb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Routing is optimized for trucks. The route provided may not be the shortest route for passenger vehicles. Origin and Destination addresses must be within the City of Chicago limits. No routes will be provided outside of the City of Chicago. Vehicle operators are required to obtain the necessary permit for operating their vehicle on Chicago streets. Receiving a route from this website does not release a vehicle operator from the requirement to obtain the necessary permit. All vehicle travel restrictions and exceptions can be found in the Municipal Code of Chicago, Chapter 9-72.
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button variant="outlined" onClick={handleDisclaimerCancel}>Cancel</Button>
                            <Button variant="contained" onClick={handleDisclaimerAccept}>I Understand</Button>
                        </Box>
                    </Box>
                </Dialog>            
            </Box>
        </>
    )
}

const DirectionsButton = (props) => {
    const setDirectionsButtonOpen = props.setDirectionsButtonOpen;
    const loggedIn = props.loggedIn;
    const setShowAlert = props.setShowAlert;
    const setMessage = props.setMessage;
    const setSeverity = props.setSeverity;
    
    const handleClick = () => {
        if (!loggedIn) {
            setMessage("In order to find truck routes, you must sign in or create a new account. Please select the profile icon in the upper right corner to sign in/create an account.");
            setSeverity("info");
            setShowAlert(true);
        } else {
            setDirectionsButtonOpen(directionButtonOpen => { return !directionButtonOpen });
        }
    }

    return (
        <Box component="div" sx={{ ml: '1rem' }}>
            <Button variant="contained" size="large" aria-label="find-routes" endIcon={<DirectionsIcon />} onClick={handleClick} sx={{
                backgroundColor: "white", color: "rgba(0,0,0,0.6)", boxShadow: "rgb(0 0 0 / 30%) 0px 1px 4px -1px", '&:hover': {
                    backgroundColor: "white",
                    boxShadow: "rgb(0 0 0 / 30%) 0px 1px 4px -1px",
                    color: "rgba(0,0,0,1)"
                },
            }}>
                Find Routes
            </Button>
        </Box>
    )
}

const AddressLookup = (props) => {
    const disableEntry = props.disableEntry;
    const updateRouteLookup = props.updateRouteLookup;
    const routeLookup = props.routeLookup;
    const title = props.title;
    const [error, setError] = React.useState(false);
    const direction = props.direction;
    const setDirection = props.setDirection;
    const selected = props.selected;
    const setSelected = props.setSelected;
    const clicklatlng = props.clicklatlng;
    const setClickLatLng = props.setClickLatLng;
    const map = useGoogleMap();
    const roads = props.roads;

    const getIcon = () => {
        if (title.toLowerCase() === "origin") {
            return 'http://maps.google.com/mapfiles/kml/paddle/go.png';
        } else {
            return 'http://maps.google.com/mapfiles/kml/paddle/stop.png';
        }
    }

    const handelOnClick = () => {
        setClickLatLng(null);
        setSelected(selected => {
            if (selected === title) return "";
            else return title;
        })
    }

    async function fetchData(routeLookup) {
        const result = await fetch(process.env.REACT_APP_API_HOST + "/route/geocode?" + new URLSearchParams({ lat: routeLookup[title].lat, lng: routeLookup[title].lng }));
        result
            .json()
            .then(res => {
                if (null !== routeLookup[title + "Address"].marker) {
                    routeLookup[title + "Address"].marker.setMap(null);
                }
                routeLookup[title + "Address"].marker = new window.google.maps.Marker({
                    position: new window.google.maps.LatLng(res.latitude, res.longitude),
                    map: map,
                    draggable: false,
                    icon: {
                        url: getIcon(),
                        scaledSize: new window.google.maps.Size(44, 44)
                    }
                })
                const value = titleCase(res.cadAddress.toLowerCase());
                routeLookup[title].crossStreet = value;
                routeLookup[title].valid = true;
                updateRouteLookup({ ...routeLookup });
            }).then(() => {
            }).catch(err => {
                console.log(err);
            });
    }

    const debounceFetchData = React.useCallback(
        _.debounce(routeLookup => {
            fetchData(routeLookup)
        }, 500),
        []
    );

    React.useEffect(() => {
        if (routeLookup && routeLookup[title] && routeLookup[title].lat) {
            debounceFetchData(routeLookup);
        } else {
            updateRouteLookup(routeLookup => {
                let address = routeLookup[title + "Address"];
                let updatedAddress = { ...address, addressNum: "", roadwayName: null };
                routeLookup[title + "Address"] = updatedAddress;
                routeLookup[title].crossStreet = "";
                return { ...routeLookup };
            })
            if (null !== routeLookup[title + "Address"].marker) {
                routeLookup[title + "Address"].marker.setMap(null);
            }
        }
    }, [routeLookup[title], title, map])

    React.useEffect(() => {
        if (selected === title && clicklatlng) {
            const addtitle = title + "Address";
            const latlng = { lat: clicklatlng.lat().toFixed(5) * 1, lng: clicklatlng.lng().toFixed(5) * 1, crossStreet: "" };
            updateRouteLookup(routeLookup => {
                const oldAddress = routeLookup[addtitle];
                return { ...routeLookup, [title]: latlng, [addtitle]: { ...oldAddress, addressNum: "", roadwayName: null, error: false } }
            });
        }
    }, [selected, clicklatlng, map, title, updateRouteLookup]);

    React.useEffect(() => {
        async function fetchData(roadwayName, addressNumber) {
            const result = await fetch(process.env.REACT_APP_API_HOST + "/route/lookupAddress?" + new URLSearchParams({ roadwayName: roadwayName, addressNumber: addressNumber }));
            result
                .json()
                .then(res => {
                    if (res.latitude === 0 || res.longitude === 0) {
                        setError(true);
                        updateRouteLookup(routeLookup => {
                            let myAddressValue = routeLookup[title + "Address"];
                            let newAddressValue = { ...myAddressValue, roadwayName: null, addressNum: "", error: true };
                            routeLookup[title + "Address"] = newAddressValue;
                            routeLookup[title].valid = false;
                            return { ...routeLookup }
                        });
                    } else {
                        setError(false);
                        updateRouteLookup(routeLookup => {
                            const latlng = { lat: res.latitude, lng: res.longitude, crossStreet: routeLookup[title].crossStreet }
                            routeLookup[title] = latlng;
                            routeLookup[title].valid = true;
                            return { ...routeLookup }
                        });
                    }
                }).then(() => {
                });
        }
        if ("" !== routeLookup[title + "Address"].addressNum && null !== routeLookup[title + "Address"].roadwayName && routeLookup[title].crossStreet === "") {
            fetchData(routeLookup[title + "Address"].roadwayName, routeLookup[title + "Address"].addressNum);
        }
    }, [routeLookup[title + "Address"]])

    const handleOnChange = (event, routeLookup, title) => {
        const elName = event.target.name;
        const elValue = event.target.value;
        let routeAddress = routeLookup[title + "Address"];
        routeAddress[elName] = elValue;
        routeLookup[title + "Address"] = { ...routeAddress };
        routeLookup[title].crossStreet = "";
        updateRouteLookup({ ...routeLookup })
    }

    const handleSelectChange = (event, routeLookup, title, newInputValue) => {
        let routeAddress = routeLookup[title + "Address"];
        routeAddress["roadwayName"] = newInputValue;
        routeLookup[title + "Address"] = { ...routeAddress };
        routeLookup[title].crossStreet = "";
        updateRouteLookup({ ...routeLookup })
    }

    const handleDirectionChange = (event, routeLookup, title) => {
        const value = event.target.value;
        if(value === "No Direction" || value === "")
            routeLookup[title+ "Direction"] = null;
        else
            routeLookup[title+ "Direction"] = value ? value.toUpperCase() : null;
        updateRouteLookup({ ...routeLookup });
        setDirection(value);
    };

    const helpText =
        title === "origin"
            ? `${titleCase(title)}: Enter an address below or manually pick the location using the map by clicking on the circle button then clicking on the target location.  Optional: For accurate routing, select the starting direction of the vehicle.`
            : `${titleCase(title)}: Enter an address below or manually pick the location using the map by clicking on the circle button then clicking on the target location.  Optional: For accurate routing, select the direction the vehicle should face at the end of the trip.`;

    const mapClickHelpText =
        title === "origin"
            ? `OPTIONAL: Click on this circle icon, then click anywhere on the map to select your Origin location. The intersection closest to your selection will appear next to this icon.`
            : `OPTIONAL: Click on this circle icon, then click anywhere on the map to select your Destination location. The intersection closest to your selection will appear next to this icon.`;

    const dirSelectHelpText =
        title === "origin"
            ? `OPTIONAL: If available, specify the starting direction for accurate routing. From the drop-down list, select the direction the vehicle will leave the Origin. Different starting directions can produce different routes.`
            : `OPTIONAL: If available, specify the ending direction for accurate routing. From the drop-down list, select the direction the vehicle will approach the destination. Different approaching directions can produce different routes.`;

    return (
        <>
            <Divider component="div" role="presentation" textAlign="left" sx={{ mb: 1 }}>
                 {/* <Tooltip enterTouchDelay={0}
                            leaveTouchDelay={3000}
                            title={helpText}>
                    <IconButton
                        size="small"
                        
                        aria-label="Direction Info"
                    >
                        <InfoIcon fontSize="small" />
                    </IconButton>
                    </Tooltip> */}
                <Typography variant="button">{titleCase(title)}</Typography>
            </Divider>
            <Grid container spacing={1}>
                <Grid item xs={4}>
                    <TextField label="Address #" type="number" size="small" name="addressNum" onChange={(e) => { handleOnChange(e, routeLookup, title) }} value={routeLookup[title + "Address"].addressNum} disabled={disableEntry} />
                </Grid>
                <Grid item xs={8}>
                    <Autocomplete
                        name="roadwayName"
                        disabled={disableEntry}
                        sx={{ width: "100%" }}
                        size="small"
                        disablePortal
                        value={routeLookup[title + "Address"].roadwayName}
                        options={roads}
                        onChange={(e, newInputValue) => { handleSelectChange(e, routeLookup, title, newInputValue) }}
                        renderInput={(params) => <TextField {...params} label="Street Name" />}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={1} sx={{ mt: 1, mb: 1 }}>
                
                <Grid item xs={1} md={0.75} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    
                    <Tooltip enterTouchDelay={0}
                            leaveTouchDelay={3000}
                            title={dirSelectHelpText}>
                    <IconButton
                        size="small"
                        aria-label="Direction Info"
                    >
                        <InfoIcon fontSize="small" />
                    </IconButton>
                    </Tooltip>
                    
                </Grid>
                <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                    <Box >
                    <InputLabel id={`${title}-direction-label`}>Direction</InputLabel>

                    <Select
                        labelId={`${title}-direction-label`}
                        value={direction}
                        onChange={(e) => handleDirectionChange(e, routeLookup, title)}
                        label="Direction"
                        disabled={disableEntry}
                        sx={{ width: "100%"}}
                    >
                        <MenuItem>Any</MenuItem>
                        {["North", "South", "East", "West"].map((dir) => (
                            <MenuItem key={dir} value={dir}>{dir}</MenuItem>
                        ))}
                    </Select>                    
                    
                    </Box>
                </FormControl>
                </Grid>
                <Grid item xs={2} sx={{display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center", display: { xs: 'none', md: "block" } }}>
                     <Tooltip enterTouchDelay={0}
                            leaveTouchDelay={3000}
                            title={mapClickHelpText}>
                    <IconButton
                        size="small"
                        aria-label="Map Select Info"
                    >
                        <InfoIcon fontSize="small" />
                    </IconButton>
                    </Tooltip>
                    <IconButton aria-label={title} component="label" sx={{ flexGrow: 1 }} title={"Click on map to select " + title} onClick={handelOnClick} disabled={disableEntry} >
                        <LocationSearchingIcon sx={{ color: selected === title ? green[500] : grey[500] }} />
                    </IconButton>
                </Grid>
                <Grid item xs={10} md={5}>
                    
                    <FormControl sx={{ width: '100%' }} variant="outlined">
                        <TextField size="small" 
                            label="Closest Cross Street" 
                            sx={{ width: "100%",'& .MuiOutlinedInput-notchedOutline': { border: 'none', } }}           
                            value={routeLookup[title].crossStreet} 
                            disabled={true} 
                            InputProps={{
                                disableUnderline: true,
                                readOnly: true,
                            }}
                        />
                        <FormHelperText error={error} id={title + "error"}>
                            {error ? "Address Not Found" : ""}
                        </FormHelperText>
                    </FormControl>
                    
                </Grid>
            </Grid>
        </>
    )
}

const RoadsSelection = (props) => {
    const setSelectedRoutes = props.setSelectedRoutes;
    const selectedRoutes = props.selectedRoutes;
    const handleChange = (e, idx) => {
        console.log(e);
        setSelectedRoutes(selectedRoutes => {
            selectedRoutes[idx] = !selectedRoutes[idx];
            return [...selectedRoutes];
        })
    }

    //Added so that only route 1 is selected by default
    React.useEffect(() => {
        if (selectedRoutes && selectedRoutes.length > 0) {
          // Check if all are false or the first one is false.
          
            setSelectedRoutes(prevRoutes => {
              const newRoutes = [...prevRoutes];
              newRoutes[0] = true;
              // (Optional) Ensure all others are false:
              for (let i = 1; i < newRoutes.length; i++) {
                newRoutes[i] = false;
              }
              return newRoutes;
            });
          
        }
      }, []); 

    return (
        <>
            {selectedRoutes.map((selectedRoute, idx) => {
                const myIdx = idx + 1;
                return <FormControlLabel control={<Checkbox size="small" checked={selectedRoute} />} label={"Route " + myIdx} onChange={(e) => handleChange(e, idx)} key={idx} />
            })}
        </>
    )
}

export {
    DirectionsButton,
    DirectionsControl
}

